import { getFontServiceParamsFromString } from '../server/utils/font-service-utils';
import { IElementNode, IWidgetElementDataNode, IWidgetElement } from '../creative/nodes';
import { WidgetEvent, CreativeEvent, TimelineEvent } from './events';
import { IProperties, WidgetProperty } from './widget-properties';
import { IWidgetContext } from './window';
import { CreativeApi } from './creative-api';
import { TimelineApi } from './timeline-api';
import { cssReset } from './css-reset';
import { EventEmitter } from '../../lib/event-emitter';
import { AdApi } from './ad-api';
import { WidgetRenderer } from './widget-renderer';
import { b64DecodeUnicode } from '../../lib/utils';
import { IWidgetElementProperty, WidgetUnits } from '../domain/widget';
import { deserializePropertyValue } from '../creative/serializer';
import { IFeed, IFeedData } from '../domain/feed';
import { FeedStore } from '../creative/feed-store';
import { isVersionedText } from '../creative/text-nodes';
import { isBannerflow, replaceOrigin, isInLocalEnvironment } from '../ad/utils/url';
import { getImageOptimizerUrl, IImageOptimizerUrlOptions } from '../ad/utils/image-optimizer';

/** Remove declaration and interface once it's approved by w3c */
declare class FontFace {
    constructor(fontFamily: string, url: string);
    load: () => Promise<FontFace>;
    display: string;
    family: string;
    featureSettings: string;
    loaded: Promise<FontFace>;
    status: string;
    stretch: string;
    style: string;
    unicodeRange: string;
    variant: string;
    weight: string;
}

/**
 * Public properties that are usable by widgets should also be defined in ./declarations/widget.ts
 */
export class Widget extends EventEmitter<WidgetEvent> {
    readonly id: string;
    x: number;
    y: number;
    width: number;
    height: number;
    properties: IProperties = {};
    time = this.elementNode.time;
    duration = this.elementNode.duration;
    isLoadedPromise: Promise<void>;

    private ad: AdApi;
    private creative: CreativeApi;
    private timeline: TimelineApi;
    isInitialized: Promise<void>;
    private isInitializedResolver: (value?: void | Promise<void> | undefined) => void;

    private previousWidgetState: Widget = {} as Widget;
    private widgetIframe: HTMLIFrameElement;
    private widgetIframeWindow: IWidgetContext;
    private widgetIframeDocument: Document;
    private blockerElement: HTMLElement;
    private feedStore?: FeedStore;

    private _mouseInteractions = false;
    inSandboxedIframe: boolean;

    set mouseInteractions(val: boolean) {
        this._mouseInteractions = val;

        // Iframes "steal" mouse events so always show the blocker element in the Design view
        if (!this._mouseInteractions || this.creative.environment === 'design-view') {
            this.blockerElement.style.display = 'block';
        }
        else {
            this.blockerElement.style.display = 'none';
        }
    }
    get mouseInteractions(): boolean {
        return this._mouseInteractions;
    }

    private _hidden = false;

    set hidden(val: boolean) {
        this._hidden = val;
        if (this.elementNode.__rootElement) {
            if (this._hidden) {
                this.elementNode.__rootElement.style.display = 'none';
            }
            else {
                this.elementNode.__rootElement.style.display = 'block';
            }
        }
    }

    get hidden(): boolean {
        return this._hidden;
    }

    constructor(
        private elementNode: IWidgetElement,
        private dataNode: IWidgetElementDataNode,
        private widgetRenderer: WidgetRenderer
    ) {
        super();
        this.x = this.elementNode.x;
        this.y = this.elementNode.y;
        this.id = this.elementNode.id;
        this.width = this.elementNode.width;
        this.height = this.elementNode.height;

        this.ad = this.widgetRenderer.adApi;
        this.creative = this.widgetRenderer.creativeApi;
        this.timeline = this.widgetRenderer.timelineApi;

        if (this.widgetRenderer.renderer.feedStore) {
            this.feedStore = this.widgetRenderer.renderer.feedStore;
        }

        this.isInitialized = new Promise<void>((resolve) => this.isInitializedResolver = resolve);

        Object.assign(this.previousWidgetState, this);
        this.elementNode.__onChange = (_, property) => this.onWidgetElementChanged(property as string);
        this.createWidgetIframe((iframe) => this.init(iframe));
    }

    private async init(iframe: HTMLIFrameElement): Promise<void> {
        if (!iframe.contentWindow) {
            this.elementNode.__rootElement!.removeChild(this.blockerElement);
            this.elementNode.__rootElement!.removeChild(this.widgetIframe);
            return;
        }

        this.inSandboxedIframe = !iframe.contentDocument;

        this.properties = this.mapCustomProperties(this.elementNode);

        this.widgetIframeWindow = iframe.contentDocument
            ? (iframe.contentWindow!.window as unknown as IWidgetContext)
            : window as unknown as IWidgetContext;

        this.widgetIframeWindow.Widget = this;
        this.widgetIframeWindow.WidgetEvent = WidgetEvent;
        this.widgetIframeWindow.CreativeEvent = CreativeEvent;
        this.widgetIframeWindow.TimelineEvent = TimelineEvent;
        this.widgetIframeWindow.Creative = this.creative;
        this.widgetIframeWindow.Timeline = this.timeline!;
        this.widgetIframeWindow.Ad = this.ad!;

        if (this.inSandboxedIframe) {
            this.widgetIframe.remove();
            this.widgetIframeDocument = this.widgetIframeWindow.document;
            this.elementNode.__rootElement!.innerHTML = b64DecodeUnicode(this.dataNode.html);
        }
        else {
            this.widgetIframeWindow.document.body.innerHTML = b64DecodeUnicode(this.dataNode.html);
            this.widgetIframeDocument = this.widgetIframe.contentDocument!;
        }

        const scripts = this.widgetIframeWindow.document.body.querySelectorAll<HTMLScriptElement>('script');
        const scriptPromises: Promise<void>[] = [];
        for (const script of Array.from(scripts)) {
            if (script.src && (isInLocalEnvironment() || script.src.startsWith('https'))) {
                const s = this.widgetIframeWindow.document.createElement('script');
                s.src = script.src;
                this.widgetIframeWindow.document.body.appendChild(s);
                let promiseResolve: (value?: PromiseLike<void>) => void;
                scriptPromises.push(
                    new Promise(resolve => {
                        promiseResolve = resolve;
                    })
                );
                s.onload = () => promiseResolve();
                s.remove();
            }
        }
        await Promise.all(scriptPromises);

        const evalstr = this.inSandboxedIframe
            ? ''
            : 'window.errorFunction = window.parent.displayErrorFromWidget; window.parent = window.top = undefined;';

        this.widgetIframeWindow.eval(evalstr +
            b64DecodeUnicode(this.dataNode.js) +
            `\n//# sourceURL=widget-${this.elementNode.id}.js`
        );
        this.widgetIframeWindow.document.dispatchEvent(new Event('DOMContentLoaded', {
            bubbles: true,
            cancelable: true
        }));

        const style = this.widgetIframeWindow.document.createElement('style');
        style.innerHTML = cssReset + b64DecodeUnicode(this.dataNode.css);
        this.widgetIframeWindow.document.head!.appendChild(style);
        this.widgetIframeWindow.dispatchEvent(new Event('load', {
            bubbles: true,
            cancelable: true
        }));

        this.setMouseEvents();
        this.setTimelineAnimationEvents();
        this.dispatchInitEvents();
        this.isInitializedResolver();

        if (this.isLoadedPromise) {
            this.widgetRenderer.widgetLoadingPromises.push(this.isLoadedPromise);
        }
    }

    private dispatchInitEvents(): void {
        const ignoredEventTypes = ['mouse', 'animation', 'transition', 'click', 'tcdata'];
        Object.keys(WidgetEvent).forEach((event: string) => {
            if (!ignoredEventTypes.filter(e => event.match(new RegExp(e, 'gi'))).length) {
                this.emit(WidgetEvent[event]);
            }
        });

        this.timeline.emit(TimelineEvent.Tick, [this.timeline.currentTime]);
    }

    private setMouseEvents(): void {
        Object.keys(WidgetEvent).forEach((event: string) => {
            if (event.startsWith('Mouse') || WidgetEvent[event] === WidgetEvent.Click) {
                this.blockerElement.addEventListener(WidgetEvent[event], this.onBlockerMouseEvent);
                this.widgetIframeWindow!.addEventListener(WidgetEvent[event], this.onWidgetMouseEvent);
            }
        });
    }

    private onWidgetMouseEvent = (mouseEvent: MouseEvent): void => {
        const event = mouseEvent.type as WidgetEvent;
        this.emit(event, [{ x: mouseEvent.clientX, y: mouseEvent.clientY }]);

        /**
         * Calculate the mouse position in the creative relative to the widget iframe.
         * We need to manually emit the correct position as the iframe will "steal" the
         * interactions if mouseInteractions is true.
        */
        if (this.mouseInteractions) {
            const offsetX = Math.round(this.x + mouseEvent.clientX);
            const offsetY = Math.round(this.y + mouseEvent.clientY);
            this.creative.emit(event as unknown as CreativeEvent, [{ x: offsetX, y: offsetY }]);
        }
    }

    private onBlockerMouseEvent = (mouseEvent: MouseEvent): void => {
        const event = mouseEvent.type as WidgetEvent;
        if (WidgetRenderer.env.STUDIO_JS && event === WidgetEvent.Click && this.creative.environment !== 'creative') {
            return;
        }

        if ((event.startsWith('mouse') || event === WidgetEvent.Click) &&
            (this.widgetRenderer.timelineApi.isPlaying && window.bfstudio?.activePage !== 'MV'))
        {
            mouseEvent.stopPropagation();

            if (event === WidgetEvent.Click) {
                this.creative.open(mouseEvent);
            }
        }

        this.emit(event, [{ x: mouseEvent.offsetX, y: mouseEvent.offsetY }]);
    }

    /**
     * The timeline events are added here instead of in TimelineApi as we need to
     * know that it's the specific widget that the event occurs for.
     */
    private setTimelineAnimationEvents(): void {
        Object.keys(WidgetEvent).forEach((event: string) => {
            if (event.indexOf('Animation') > -1 || event.indexOf('Transition') > -1) {
                this.timeline.animator.on(WidgetEvent[event], (element: IElementNode) => {
                    if (element.id === this.id) {
                        this.emit(WidgetEvent[event], [element]);
                    }
                });
            }
        });
    }

    /**
     * Check if the values of the widget has been changed
     */
    private checkValueDiff(property: WidgetProperty, propertyToCheck: string): boolean {

        function isObject(obj: any): boolean {
            return obj != null && obj.constructor.name === 'Object';
        }

        function isFunction(val: any): boolean {
            return val instanceof Function || typeof val === 'function';
        }

        if (property !== propertyToCheck) {
            return false;
        }

        if (property === 'customProperties') {
            for (const i in this.properties) {
                const newValue = this.properties[i];
                const prevValue = this.previousWidgetState.properties[i];

                if ((isObject(newValue) && (newValue as IFeedHelper).load) && (isObject(prevValue) && (prevValue as IFeedHelper).load)) {
                    if (newValue.id !== prevValue.id) {
                        this.previousWidgetState.properties[i] = this.properties[i];
                        return true;
                    }
                }
                else if (isObject(newValue) && isObject(prevValue)) {
                    if (Array.isArray(newValue)) {
                        for (let i = 0; i < newValue.length; i++) {
                            if (typeof prevValue[i] !== 'undefined' && newValue[i] !== prevValue[i]) {
                                return true;
                            }
                        }
                    }
                    else {
                        for (const value in newValue) {
                            if (isFunction(newValue[value])) {
                                continue;
                            }
                            if (!(value in prevValue)) {
                                return true;
                            }
                            else if (newValue[value] !== prevValue[value]) {
                                return true;
                            }
                        }
                    }
                }
                else if (newValue !== prevValue) {
                    this.previousWidgetState.properties[i] = this.properties[i];
                    return true;
                }
            }
        }
        else if (this.previousWidgetState[property] !== this[property]) {
            this.previousWidgetState[property] = this[property];
            return true;
        }
        return false;
    }

    /**
     * Maps the customProperties to a property list ({key: value}) e.g:
     *
     * { label: "label", type: "custom:key", unit: "string", value: "hello"} => { key: 'hello' }
     */
    private mapCustomProperties(viewElement: IWidgetElement): IProperties {
        const properties = {};
        if (viewElement.customProperties) {
            (viewElement.customProperties as any).forEach((property: IWidgetElementProperty) => {
                const key = property.name.replace('custom:', ''); // TODO, find some smart way here
                const unit = property.unit as WidgetUnits;

                const isVersionedProperty = isVersionedText(property.value);

                const value = deserializePropertyValue(unit === 'text' && isVersionedProperty
                    ? 'content'
                    : unit, property.value) as any;

                if (unit === 'text') {
                    if (typeof value === 'object' && !Array.isArray(value)) {
                        properties[key] = value.text;
                    }
                    else {
                        properties[key] = value;
                    }
                }
                else if (unit === 'select') {
                    properties[key] = (value as any[])
                        .find((val: { selected: boolean, value: string }) => val.selected).value;
                }
                else if (unit === 'color') {
                    // Don't deserialize the color value, we just want the raw color value, e.g rgba(255, 0, 0, 0)
                    properties[key] = property.value;
                }
                else if (unit === 'font') {
                    properties[key] = value;
                    // Prefix in order to make the DOM validate it as string as id can start with a number
                    properties[key].id = 'font-' + value.id;
                    if (value.id !== 'undefined' && value.src !== 'undefined') {
                        if (!WidgetRenderer.env.STUDIO_JS) {
                            const fontPath = encodeURIComponent(properties[key].src);
                            const fontServiceUri = `${WidgetRenderer.env.FONTSERVICE_API_ORIGIN}/api/v2/font?u=${fontPath}`;
                            properties[key].src = fontServiceUri;
                        }
                    }
                    else {
                        properties[key] = {
                            fontFamilyId: '',
                            id: '',
                            src: '',
                            style: '',
                            weight: ''
                        };
                    }

                    properties[key].toFontFace = (text?: string, name?: string): { fontface: string, url: string } | undefined => {

                        if (name && !name.match(/^[a-z|A-Z]/)) {
                            console.error('Custom font name must start with a letter a-Z.');
                            return;
                        }

                        const { textParams } = text ? getFontServiceParamsFromString(text) : { textParams: undefined };

                        if (!this.inSandboxedIframe && !isInLocalEnvironment() && !isBannerflow(window.location.href)) {
                            properties[key].src = replaceOrigin(properties[key].src, this.widgetRenderer.renderer.env.FONTSERVICE_API_ORIGIN!);
                        }

                        const url = properties[key].src + (textParams && !WidgetRenderer.env.STUDIO_JS ? textParams : '');

                        return {
                            fontface: `@font-face {
                            font-family: "${name || properties[key].id}";
                            src: url("${url}");
                            font-style: ${properties[key].style};
                            font-weight: ${properties[key].weight};
                        }`,
                            url
                        };
                    };

                    properties[key].createFontFace = async (text?: string, name?: string): Promise<FontFace | void> => {

                        if (name && !name.match(/^[a-z|A-Z]/)) {
                            console.error('Custom font name must start with a letter a-Z.');
                            return;
                        }

                        const fontId = name || properties[key].id;
                        const { fontface, url } = properties[key].toFontFace(text, name);

                        const fontLoader = new FontFace(fontId, `url('${url}')`) as FontFace;

                        try {
                            this.widgetRenderer.widgetLoadingPromises.push(fontLoader.loaded);
                            const loadedFontFace = await fontLoader.load();
                            this.widgetIframeDocument['fonts'].add(loadedFontFace);
                            return loadedFontFace;
                        }
                        catch {
                            if (!this.widgetIframeDocument.querySelector('#' + fontId)) {
                                const style = document.createElement('style');
                                style.id = name || properties[key].id;
                                style.innerHTML = fontface;
                                this.widgetIframeDocument!.head!.appendChild(style);
                            }
                        }
                    };
                }
                else if (unit === 'image') {
                    properties[key] = !value.url || value.url === 'undefined'
                        ? undefined
                        : value.url;

                    if (!this.inSandboxedIframe && !isInLocalEnvironment() && typeof properties[key] === 'string' && !isBannerflow(window.location.href)) {
                        properties[key] = replaceOrigin(value.url, this.widgetRenderer.ad.getOrigin()!);
                    }
                }
                else if (unit === 'feed') {
                    const feedHelper = {
                        loaded: false,
                        load: {},
                        data: {},
                        id: (value as IFeed).id
                    } as IFeedHelper;

                    const feedLoader = (): Promise<IFeedData> => {
                        return new Promise<IFeedData>(async (resolve: any, reject: any): Promise<void> => {
                            try {

                                const id = (value as IFeed).id;

                                if (!id) {
                                    reject('Could not fetch feed. No feed is selected.');
                                    return;
                                }

                                if (feedHelper.loaded) {
                                    resolve(feedHelper.data);
                                }
                                else {
                                    feedHelper.data = await this.feedStore!.fetchFeedData(id, true);
                                    feedHelper.loaded = true;
                                    resolve(feedHelper.data);
                                }
                            }
                            catch (e) {
                                reject(e);
                            }
                        });
                    };

                    feedHelper.load = feedLoader;
                    properties[key] = feedHelper;
                }
                else {
                    properties[key] = value;
                }
            });
        }
        return properties;
    }

    private onWidgetElementChanged(property: string): void {

        // Sync the property value of the updated elementNode with values of the widget
        this.syncWidgetValueWithElementNodeValue(property);

        if (this.checkValueDiff('x', property) || this.checkValueDiff('y', property)) {
            this.emit(WidgetEvent.Move);
        }

        if (this.checkValueDiff('width', property) || this.checkValueDiff('height', property)) {
            this.emit(WidgetEvent.Resize);
        }

        if (this.checkValueDiff('customProperties', property)) {
            this.emit(WidgetEvent.PropertyChanged);
        }

        // Keep track of the current state for future change checks
        Object.assign(this.previousWidgetState, this);
    }

    /**
     * @param  {string} property
     * Updates the property values of the widget to match the values in the elementNode
     */
    private syncWidgetValueWithElementNodeValue(property: string): void {
        if (property === 'customProperties') {
            this.properties = this.mapCustomProperties(this.elementNode);
        }
        else {
            if (!(property in this)) {
                return;
            }

            // Use the internal property rather than the one on elementNode
            if (property === 'hidden') {
                return;
            }

            this[property] = this.elementNode[property];
        }
    }

    getOptimizedImageUrl(url: string, options: IImageOptimizerUrlOptions): string {
        if (isInLocalEnvironment()) {
            return url;
        }

        const imageOptimizerOrigin = WidgetRenderer.env.STUDIO_JS
            ? undefined
            : WidgetRenderer.env.IMAGE_OPTIMIZER_ORIGIN;

        return getImageOptimizerUrl(url, { ...options, imageOptimizerOrigin });
    }

    emitCustomEvent(event: string, data?: any): void {
        this.creative.emit('customEvent:' + event, [data]);
    }

    onCustomEvent(event: string, callback: (response?: any) => void): void {
        this.creative.on('customEvent:' + event, (data) => {
            callback(data);
        });
    }

    removeCustomEvent(event: string, callback: (response?: any) => void): void {
        this.creative.off('customEvent:' + event, callback);
    }

    private createWidgetIframe(callback: (iframe: HTMLIFrameElement) => void): void {
        const iframe = document.createElement('iframe');
        iframe.id = `widget-${this.elementNode.id}`;
        iframe.style.width = '100%';
        iframe.style.height = '100%';
        iframe.style.maxWidth = '100%';
        iframe.style.outline = 'none';
        iframe.style.border = 'none';
        iframe.style.display = 'block';
        iframe.style.position = 'absolute';
        iframe.style.left = '0';
        iframe.style.top = '0';
        iframe.style.transformOrigin = '0 0';
        iframe.style.transform = 'none';
        iframe.style.filter = 'none';
        iframe.src = 'about:blank';
        iframe.addEventListener('load', () => setTimeout(() => callback(iframe), 0));

        // Used to catch interactions if mouseInteractions is off
        const blocker = document.createElement('div') as HTMLElement;
        blocker.style.position = 'absolute';
        blocker.style.top = '0px';
        blocker.style.left = '0px';
        blocker.classList.add('widget-interaction-blocker');
        blocker.style.width = '100%';
        blocker.style.height = '100%';
        blocker.style.display = 'block';
        blocker.style.zIndex = '1';

        this.blockerElement = blocker;
        this.elementNode.__rootElement!.appendChild(blocker);

        this.widgetIframe = iframe;
        this.elementNode.__rootElement!.appendChild(iframe);
    }

    destroy(): void {
        this.clearEvents();

        try {
            Object.keys(WidgetEvent).forEach((event: string) => {
                if (event.startsWith('Mouse') || WidgetEvent[event] === WidgetEvent.Click) {
                    this.blockerElement.removeEventListener(WidgetEvent[event], this.onBlockerMouseEvent);
                    this.widgetIframeWindow!.removeEventListener(WidgetEvent[event], this.onWidgetMouseEvent);
                }
            });

            Object.keys(WidgetEvent).forEach((event: string) => {
                if (event.indexOf('Animation') > -1 || event.indexOf('Transition') > -1) {
                    this.timeline.animator.off(WidgetEvent[event]);
                }
            });
        } catch (e) { }

    }
}

export interface IFeedHelper {
    id: string;
    /**
    * Returns a promise which resolves with FeedData
    */
    load: () => Promise<IFeedData>;
    data: {};
    loaded: boolean;
}
